import { useEffect } from "react";

import { useSession } from "@vega-api/custom/SessionContext";
import { useGetPendingTasks } from "@vega-api/generated/tasks-v1/tasks-v1";
import { useLocaleStorage } from "@vega-core/hooks/useLocaleStorage";
import { isTruthy } from "@vega-core/utils/isTruthy";

const TASKS_LOCAL_STORAGE_KEY = "tasksState";

type TaskState = {
  completed: boolean;
};

type UseTaskStateArgs = {
  enabled: boolean;
};

export function useTaskState({ enabled }: UseTaskStateArgs) {
  const [tasksState, setTasksState] = useLocaleStorage<TaskState>(
    TASKS_LOCAL_STORAGE_KEY,
    { completed: false },
  );

  const { user } = useSession();

  const {
    data: pendingTasks = [],
    isLoading,
    isFetched,
    refetch,
    isError,
    isSuccess,
  } = useGetPendingTasks(
    {},
    {
      query: {
        enabled: enabled && isTruthy(user) && !tasksState.completed,
      },
    },
  );

  const hasPendingTasks = isSuccess && pendingTasks.length > 0;

  useEffect(
    function updateTasksStateOnFetch() {
      if (isSuccess && !hasPendingTasks) {
        setTasksState({ completed: true });
      }
    },
    [isSuccess, hasPendingTasks, setTasksState],
  );

  return {
    pendingTasks,
    isLoading,
    isError,
    isFetched,
    hasPendingTasks,
    isCompleted: tasksState.completed,
    refetch,
  };
}
